import {type AtpAgent} from '@atproto/api'
import {type I18n} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {VIDEO_SERVICE_DID} from '#/lib/constants'
import {UploadLimitError} from '#/lib/media/video/errors'
import {
  getServiceAuthAudFromDidDoc,
  getServiceAuthAudFromUrl,
} from '#/lib/strings/url-helpers'
import {logger} from '#/logger'
import {createVideoAgent} from './util'

export async function getServiceAuthToken({
  agent,
  aud,
  lxm,
  exp,
}: {
  agent: AtpAgent
  aud?: string
  lxm: string
  exp?: number
}) {
  const {data: serviceAuth} = await agent.com.atproto.server.getServiceAuth({
    aud: aud ?? (await getPdsServiceAuthAud(agent)),
    lxm,
    exp,
  })
  return serviceAuth.token
}

/**
 * Audience for a service auth token addressed to the user's own PDS.
 *
 * Prefer the PDS endpoint from the account's DID document over
 * `agent.dispatchUrl`: behind an entryway the agent dispatches to the entryway
 * host, while the DID document (and therefore any service verifying the
 * token, such as video.bsky.app) names the PDS that actually holds the repo.
 * Falls back to `dispatchUrl` when the DID document cannot be resolved.
 */
async function getPdsServiceAuthAud(agent: AtpAgent): Promise<string> {
  const did = agent.did
  if (did) {
    try {
      const {data} = await agent.com.atproto.repo.describeRepo({repo: did})
      const didDocAud = getServiceAuthAudFromDidDoc(data.didDoc)
      if (didDocAud) {
        return didDocAud
      }
      logger.warn('DID document has no PDS endpoint, using dispatch URL', {
        did,
      })
    } catch (e) {
      logger.warn('Failed to resolve PDS from DID document', {
        did,
        safeMessage: e,
      })
    }
  }
  const dispatchAud = getServiceAuthAudFromUrl(agent.dispatchUrl)
  if (!dispatchAud) {
    throw new Error('Agent does not have a PDS URL')
  }
  return dispatchAud
}

export async function getVideoUploadLimits(agent: AtpAgent, i18n: I18n) {
  const token = await getServiceAuthToken({
    agent,
    lxm: 'app.bsky.video.getUploadLimits',
    aud: VIDEO_SERVICE_DID,
  })
  const videoAgent = createVideoAgent()
  const {data: limits} = await videoAgent.app.bsky.video
    .getUploadLimits({}, {headers: {Authorization: `Bearer ${token}`}})
    .catch(err => {
      if (err instanceof Error) {
        throw new UploadLimitError(err.message)
      } else {
        throw err
      }
    })

  if (!limits.canUpload) {
    if (limits.message) {
      throw new UploadLimitError(limits.message)
    } else {
      throw new UploadLimitError(
        i18n._(
          msg`You have temporarily reached the limit for video uploads. Please try again later.`,
        ),
      )
    }
  }
}
